import { compileRideProgress, type RidePoint } from "./ride";

/** Trailing window in recorded ride seconds, independent of film duration. */
export const DEFAULT_SPEED_WINDOW_SECONDS = 4;

/** Compiles distance over time for deterministic, time-weighted current speed. */
export function compileRideSpeed(
  points: readonly RidePoint[],
  windowSeconds = DEFAULT_SPEED_WINDOW_SECONDS,
): (recordedAt: string) => number {
  if (!Number.isFinite(windowSeconds) || windowSeconds <= 0) {
    throw new RangeError("Speed window must be a positive finite number.");
  }
  const route = compileRideProgress(points);
  const times = points.map(point => Date.parse(point.recordedAt));
  const distances = [0];
  const segmentStarts = [0];
  for (let index = 1; index < points.length; index += 1) {
    const validInterval = times[index]! > times[index - 1]! && !points[index]!.segmentStart;
    const distance = validInterval
      ? (route.distanceFractions[index]! - route.distanceFractions[index - 1]!) * route.totalDistanceKilometers
      : 0;
    distances.push(distances[index - 1]! + distance);
    segmentStarts.push(points[index]!.segmentStart ? index : segmentStarts[index - 1]!);
  }

  // Select the last sample at or before a time, including duplicate timestamps.
  const indexAt = (time: number): number => {
    let low = 0;
    let high = times.length;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (times[middle]! <= time) low = middle + 1;
      else high = middle;
    }
    return Math.max(0, low - 1);
  };
  const distanceAt = (time: number, index: number): number => {
    const next = index + 1;
    if (next === times.length || points[next]!.segmentStart) return distances[index]!;
    const fraction = (time - times[index]!) / (times[next]! - times[index]!);
    return distances[index]! + fraction * (distances[next]! - distances[index]!);
  };

  return (recordedAt) => {
    const requestedTime = Date.parse(recordedAt);
    if (!Number.isFinite(requestedTime)) throw new TypeError("Speed requires a valid recorded timestamp.");
    const time = Math.min(times.at(-1)!, Math.max(times[0]!, requestedTime));
    const index = indexAt(time);
    if (points[index + 1]?.segmentStart && time > times[index]!) return 0;
    const start = Math.max(times[segmentStarts[index]!]!, time - windowSeconds * 1_000);
    if (start === time) return 0;
    const distance = distanceAt(time, index) - distanceAt(start, indexAt(start));
    return distance * 3_600_000 / (time - start);
  };
}
